import React, { useContext, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Save, HelpCircle, Cog, Database, Download, Upload, AlertTriangle, Server, FileText, CheckCircle2, Loader2, PlusCircle, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { SettingsConfig } from '../Settings';
import { AppContext } from '@/App';
import { Alert, AlertDescription } from '../ui/alert';
import { apiClient } from '@/Utils/apiClient';
import { useAuth } from '../AuthContext';
import { errorResponse } from '@/Utils/helpers';

interface SystemSettingsProps {
  settings: SettingsConfig['system'];
  onUpdate: (settings: Partial<SettingsConfig['system']>) => void;
  onSave: () => void;
  hasUnsavedChanges: boolean;
}
interface ChartConfigData {
  id: string;
  chartType: string;
  reviewNeeded: boolean;
  approvedReviewPercentage?: number;
  sendForCorrectionReviewPercentage?: number;
  description: string;
  isActive: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Note: Kept the typo "sectoin" as per your original code to ensure it matches your specific requirement
const AVAILABLE_CHART_TYPES = ['CSC', 'DLS', 'AIDE', 'SN'];

export function SystemSettings({ settings, onUpdate, onSave, hasUnsavedChanges }: SystemSettingsProps) {
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const { token } = useAuth();

  const [isExporting, setIsExporting] = useState(false);
  const { sideBarItems, setCurrentView } = useContext(AppContext);

  const settingMenuPermission = sideBarItems.find((item: any) => item.name === "Settings");

  const [selectedChartType, setSelectedChartType] = useState<string>("");
  const [chartConfig, setChartConfig] = useState<ChartConfigData | null>(null);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [isSavingChart, setIsSavingChart] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleSave = () => {
    onSave();
    toast.success('System settings saved successfully');
  };

  // --- HELPER FUNCTION: EXTRACTS CSC/DLS FROM LONG STRING ---
  const getApiChartType = (type: string) => {
    if (!type) return "";
    // This splits "CSC - section 17" into ["CSC", "section 17"] and returns "CSC"
    // Also works for "AIDE" (returns "AIDE")
    return type.split(' - ')[0]?.trim() || "";
  };

  useEffect(() => {
    if (!selectedChartType) {
      setChartConfig(null);
      return;
    }

    const fetchConfig = async () => {
      setIsLoadingChart(true);
      try {
        // 1. Convert selected type to short code (CSC, DLS)
        const apiEndpointType = getApiChartType(selectedChartType);
        
        // Simulating the GET request: GET /charts/config/CSC
        const response = await apiClient(`charts/config/${apiEndpointType}`, {
          method: 'GET',
          token
        });

        const result = await response.json();

        // --- CASE 2: API ERROR ---
        if (!result.success) {
          throw new Error(result.error);
        }

        // --- CASE 3: CONFIG FOUND ---
        setIsCreating(false);
        setChartConfig(result.data);
      }
      catch (error) {
        console.log(error)
        const errMsg = errorResponse(error);
        if (errMsg.toLowerCase().includes("not found")) {
          setIsCreating(true);
          setChartConfig({
            id: '',
            chartType: selectedChartType, // Keep full name for UI display
            reviewNeeded: false,
            approvedReviewPercentage: 0,
            sendForCorrectionReviewPercentage: 0,
            description: '',
            isActive: true
          });
        } else {
          toast.error(`Error fetching data: ${errMsg}`)
        }

      } finally {
        setIsLoadingChart(false);
      }
    };

    fetchConfig();
  }, [selectedChartType]);

  // 2. Save data to API
  const handleSaveConfig = async () => {
    if (!chartConfig) return;

    setIsSavingChart(true);
    try {
      // 1. Get Short Code
      const apiEndpointType = getApiChartType(chartConfig.chartType);

      // Simulating the POST request
      const response = await apiClient(`charts/config/${apiEndpointType}`, {
        method: 'PUT',
        token,
        body: {
          reviewNeeded: chartConfig.reviewNeeded,
          approvedReviewPercentage: chartConfig.approvedReviewPercentage,
          sendForCorrectionReviewPercentage: chartConfig.sendForCorrectionReviewPercentage,
          description: chartConfig.description,
          isActive: chartConfig.isActive
        }
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error)

      toast.success(`${chartConfig.chartType} configuration saved successfully`);
      
      
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setIsSavingChart(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!chartConfig) return;

    if (!chartConfig.description || chartConfig.description.trim() === "") {
      toast.error("Description cannot be empty");
      return;  // STOP here
    }

    setIsSavingChart(true);

    try {
      // 1. Get Short Code for the Payload
      const apiPayloadType = getApiChartType(chartConfig.chartType);

      const response = await apiClient(`charts/config`, {
        method: 'POST',
        token,
        body: {
          chartType: apiPayloadType, // Send "CSC" instead of "CSC - section 17"
          reviewNeeded: chartConfig.reviewNeeded,
          approvedReviewPercentage: chartConfig.approvedReviewPercentage,
          sendForCorrectionReviewPercentage: chartConfig.sendForCorrectionReviewPercentage,
          description: chartConfig.description,
          isActive: chartConfig.isActive
        }
      });

      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      toast.success(`${chartConfig.chartType} configuration created successfully`);

      // switch UI to update mode
      setIsCreating(false);
      
      // Update state, but ensure we keep the UI consistent if needed
      setChartConfig(result.data);

    } catch (error) {
      const errMsg = errorResponse(error);
      toast.error(`Failed to create configuration: ${errMsg}`);
    } finally {
      setIsSavingChart(false);
    }
  };


  // 3. Local Field Updates
  const updateLocalChartState = (field: keyof ChartConfigData, value: any) => {
    if (!chartConfig) return;
    setChartConfig(prev => prev ? ({ ...prev, [field]: value }) : null);
  };

  const handleGlobalSave = () => {
    onSave();
    toast.success('System settings saved successfully');
  };

  // ... (Existing Maintenance & Export Handlers remain the same) ...
  const handleMaintenanceToggle = (enabled: boolean) => {
    if (enabled) {
      setShowMaintenanceDialog(true);
    } else {
      onUpdate({ maintenanceMode: false });
      toast.success('Maintenance mode disabled');
    }
  };

  const confirmMaintenanceMode = () => {
    onUpdate({ maintenanceMode: true });
    setShowMaintenanceDialog(false);
    toast.warning('Maintenance mode enabled. System access is restricted.');
  };

  const handleDataExport = async () => {
    setIsExporting(true);

    // Simulate export process
    setTimeout(() => {
      setIsExporting(false);
      toast.success('Data export completed successfully');
    }, 3000);
  };

  const getStorageUsage = () => {
    // Mock storage data
    return {
      used: 145.7,
      total: 500,
      percentage: 29.14
    };
  };

  const storage = getStorageUsage();

  return (
    <div className="space-y-6">
      {(!settingMenuPermission?.userPermissions?.includes("settings:manage_system")) ? (
        <div className="p-6">
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              <div>You don't have permission to access <b>System Settings.</b></div>
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <>
          {/* System Status */}
          {/* <Card>

            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]" >
                <Server className="w-4 h-4" />
                System Status
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Current system health and operational status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-green-900" style={{ fontSize: '16px', fontWeight: 600 }}>
                    Operational
                  </p>
                  <p className="text-green-700" style={{ fontSize: '10px' }}>
                    System Status
                  </p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-900" style={{ fontSize: '16px', fontWeight: 600 }}>
                    99.8%
                  </p>
                  <p className="text-blue-700" style={{ fontSize: '10px' }}>
                    Uptime (30 days)
                  </p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-orange-900" style={{ fontSize: '16px', fontWeight: 600 }}>
                    {settings.maintenanceMode ? 'ON' : 'OFF'}
                  </p>
                  <p className="text-orange-700" style={{ fontSize: '10px' }}>
                    Maintenance Mode
                  </p>
                </div>
              </div>
            </CardContent>

          </Card> */}

          {/* --- NEW UPDATED CHART CONFIGURATION SECTION --- */}
         <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <FileText className="w-4 h-4" />
                Chart Type Configuration
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Auditor Review Status - Configuration.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* 1. SELECTOR */}
              <div className="space-y-2">
                <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Select Chart Type</Label>
                <Select value={selectedChartType} onValueChange={setSelectedChartType}>
                  <SelectTrigger className="w-full md:w-[300px]">
                    <SelectValue placeholder="Select a chart type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_CHART_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 2. DYNAMIC CONTENT AREA */}
              <div className="min-h-[200px]">
                {isLoadingChart ? (
                   <div className="h-full flex flex-col items-center justify-center p-8 space-y-3">
                       <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                       <p className="text-sm text-gray-500">Fetching configuration...</p>
                   </div>
                ) : chartConfig ? (
                  <div className="border rounded-lg p-5 bg-gray-50/50 space-y-5 animate-in fade-in duration-300">
                    
                    {/* Header */}
                    <div className="border-b pb-4 flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-sm flex items-center gap-2">
                          {chartConfig.chartType}
                          {/* {isCreating ? (
                             <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">New Config</Badge>
                          ) : (
                             chartConfig.isActive ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">Active</Badge>
                             ) : (
                                <Badge variant="secondary">Inactive</Badge>
                             )
                          )} */}
                        </h3>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className={`space-y-4 ${!chartConfig.isActive ? 'opacity-60' : ''}`}>
                        
                        {/* Review Needed Toggle */}
                        <div className="flex flex-row items-center justify-between rounded-lg border p-3 bg-white shadow-sm">
                          <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Review Needed</Label>
                            <p className="text-xs text-gray-500">
                              Enable for manual auditor review; disable to allow automatic auditor review
                            </p>
                          </div>
                          <Switch
                            checked={chartConfig.reviewNeeded}
                            onCheckedChange={(c) => {
                              updateLocalChartState('reviewNeeded', c);
                              // Auto-set percentages: If reviewNeeded is true, set both percentages to 100
                              if (c === true) {
                                updateLocalChartState('approvedReviewPercentage', 100);
                                updateLocalChartState('sendForCorrectionReviewPercentage', 100);
                              }
                            }}
                            disabled={!chartConfig.isActive}
                          />
                        </div>

                        {/* Approved Review Percentage Toggle */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium">Approved Review Percentage</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <HelpCircle className="w-3 h-3 text-gray-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs text-xs">
                                    {chartConfig.reviewNeeded 
                                      ? "When Review Needed is enabled, percentage defaults to 100% (all Approved charts require review)"
                                      : "Percentage of Approved charts that will require human review. If Review Needed is false, this percentage determines how many Approved charts need review. If set to 100%, all Approved charts will require review."}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentValue = chartConfig.approvedReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0);
                                const newValue = Math.max(0, currentValue - 1);
                                updateLocalChartState('approvedReviewPercentage', newValue);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded || (chartConfig.approvedReviewPercentage ?? 0) <= 0}
                              className="h-8 w-8 p-0"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="relative flex items-center w-[60px]">
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={chartConfig.approvedReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0)}
                                onChange={(e) => {
                                  let inputValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
                                  // Remove leading zeros (except for single "0" or empty)
                                  if (inputValue && inputValue.length > 1 && inputValue.startsWith('0')) {
                                    inputValue = inputValue.replace(/^0+/, '') || '0';
                                  }
                                  // Parse and validate
                                  const numValue = inputValue === '' ? 0 : parseInt(inputValue, 10);
                                  if (!isNaN(numValue)) {
                                    const clampedValue = Math.max(0, Math.min(100, numValue));
                                    updateLocalChartState('approvedReviewPercentage', clampedValue);
                                  } else if (inputValue === '') {
                                    updateLocalChartState('approvedReviewPercentage', 0);
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseInt(e.target.value, 10) || 0;
                                  const clampedValue = Math.max(0, Math.min(100, value));
                                  updateLocalChartState('approvedReviewPercentage', clampedValue);
                                }}
                                disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                                className="h-8 px-2 pr-6 text-sm font-medium text-center bg-gray-50 border border-gray-200 rounded-md focus:bg-white"
                              />
                              <span className="absolute right-1.5 text-sm font-medium text-gray-500 pointer-events-none">%</span>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentValue = chartConfig.approvedReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0);
                                const newValue = Math.min(100, currentValue + 1);
                                updateLocalChartState('approvedReviewPercentage', newValue);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded || (chartConfig.approvedReviewPercentage ?? 100) >= 100}
                              className="h-8 w-8 p-0"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                updateLocalChartState('approvedReviewPercentage', 0);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                              className="h-8 px-3 text-xs"
                            >
                              0%
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                updateLocalChartState('approvedReviewPercentage', 100);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                              className="h-8 px-3 text-xs"
                            >
                              100%
                            </Button>
                          </div>
                          {chartConfig.reviewNeeded === false && (chartConfig.approvedReviewPercentage ?? 0) > 0 && (chartConfig.approvedReviewPercentage ?? 0) < 100 && (
                            <p className="text-xs text-gray-500">
                              {chartConfig.approvedReviewPercentage}% of Approved charts will be randomly selected for human review.
                            </p>
                          )}
                        </div>

                        {/* Send for Correction Review Percentage Toggle */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium">Send for Correction Review Percentage</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <HelpCircle className="w-3 h-3 text-gray-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs text-xs">
                                    {chartConfig.reviewNeeded 
                                      ? "When Review Needed is enabled, percentage defaults to 100% (all Send for Correction charts require review)"
                                      : "Percentage of Send for Correction charts that will require human review. If Review Needed is false, this percentage determines how many Send for Correction charts need review. If set to 100%, all Send for Correction charts will require review."}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentValue = chartConfig.sendForCorrectionReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0);
                                const newValue = Math.max(0, currentValue - 1);
                                updateLocalChartState('sendForCorrectionReviewPercentage', newValue);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded || (chartConfig.sendForCorrectionReviewPercentage ?? 0) <= 0}
                              className="h-8 w-8 p-0"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="relative flex items-center w-[60px]">
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={chartConfig.sendForCorrectionReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0)}
                                onChange={(e) => {
                                  let inputValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
                                  // Remove leading zeros (except for single "0" or empty)
                                  if (inputValue && inputValue.length > 1 && inputValue.startsWith('0')) {
                                    inputValue = inputValue.replace(/^0+/, '') || '0';
                                  }
                                  // Parse and validate
                                  const numValue = inputValue === '' ? 0 : parseInt(inputValue, 10);
                                  if (!isNaN(numValue)) {
                                    const clampedValue = Math.max(0, Math.min(100, numValue));
                                    updateLocalChartState('sendForCorrectionReviewPercentage', clampedValue);
                                  } else if (inputValue === '') {
                                    updateLocalChartState('sendForCorrectionReviewPercentage', 0);
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseInt(e.target.value, 10) || 0;
                                  const clampedValue = Math.max(0, Math.min(100, value));
                                  updateLocalChartState('sendForCorrectionReviewPercentage', clampedValue);
                                }}
                                disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                                className="h-8 px-2 pr-6 text-sm font-medium text-center bg-gray-50 border border-gray-200 rounded-md focus:bg-white"
                              />
                              <span className="absolute right-1.5 text-sm font-medium text-gray-500 pointer-events-none">%</span>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentValue = chartConfig.sendForCorrectionReviewPercentage ?? (chartConfig.reviewNeeded ? 100 : 0);
                                const newValue = Math.min(100, currentValue + 1);
                                updateLocalChartState('sendForCorrectionReviewPercentage', newValue);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded || (chartConfig.sendForCorrectionReviewPercentage ?? 100) >= 100}
                              className="h-8 w-8 p-0"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                updateLocalChartState('sendForCorrectionReviewPercentage', 0);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                              className="h-8 px-3 text-xs"
                            >
                              0%
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                updateLocalChartState('sendForCorrectionReviewPercentage', 100);
                              }}
                              disabled={!chartConfig.isActive || chartConfig.reviewNeeded}
                              className="h-8 px-3 text-xs"
                            >
                              100%
                            </Button>
                          </div>
                          {chartConfig.reviewNeeded === false && (chartConfig.sendForCorrectionReviewPercentage ?? 0) > 0 && (chartConfig.sendForCorrectionReviewPercentage ?? 0) < 100 && (
                            <p className="text-xs text-gray-500">
                              {chartConfig.sendForCorrectionReviewPercentage}% of Send for Correction charts will be randomly selected for human review.
                            </p>
                          )}
                        </div>

                        {/* Description Input */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Description</Label>
                          <Textarea
                            value={chartConfig.description}
                            onChange={(e) => updateLocalChartState('description', e.target.value)}
                            placeholder="Enter the description..."
                            className="min-h-[100px] bg-white text-sm"
                            disabled={!chartConfig.isActive}
                          />
                        </div>

                        {/* Metadata Footer */}
                        {chartConfig.updatedAt && (
                            <p className="text-[10px] text-gray-400 text-right italic">
                                Last updated: {new Date(chartConfig.updatedAt).toLocaleDateString()}
                            </p>
                        )}

                        {/* API SAVE BUTTON */}
                        <div className="flex justify-end pt-2">
                         <Button
                          onClick={isCreating ? handleCreateConfig : handleSaveConfig}
                          disabled={isSavingChart}
                          className="w-full md:w-auto"
                        >
                          {isSavingChart ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {isCreating ? 'Creating...' : 'Updating...'}
                            </>
                          ) : (
                            <>
                              {isCreating ? (
                                  <>
                                    <PlusCircle className="w-4 h-4 mr-2" />
                                    Create Configuration
                                  </>
                              ) : (
                                  <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Update Configuration
                                  </>
                              )}
                            </>
                          )}
                        </Button>
                        </div>
                    </div>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="h-40 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-gray-50 text-gray-400">
                      <FileText className="w-8 h-8 mb-2 opacity-50" />
                      <p className="text-sm">Select a chart type to load configuration</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          {/* Maintenance Mode */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]" >
                <AlertTriangle className="w-4 h-4" />
                Maintenance Mode
              </CardTitle>
              <CardDescription className='[14px]'>
                Control system availability for maintenance windows
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      Enable Maintenance Mode
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="w-3 h-3 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs" style={{ fontSize: '10px' }}>
                            Restricts system access to administrators only for maintenance activities
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-gray-600" style={{ fontSize: '9.6px' }}>
                    When enabled, only administrators can access the system
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {settings.maintenanceMode && (
                    <Badge variant="destructive" style={{ fontSize: '9.6px' }}>
                      Active
                    </Badge>
                  )}
                  <Switch
                    checked={settings.maintenanceMode}
                    onCheckedChange={handleMaintenanceToggle}
                  />
                </div>
              </div>

              {settings.maintenanceMode && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                    Maintenance Mode is Currently Active
                  </p>
                  <p className="text-red-700" style={{ fontSize: '10px' }}>
                    Only administrators can access the system. Regular users will see a maintenance message.
                  </p>
                </div>
              )}
            </CardContent>
          </Card> */}

          {/* Logging Configuration */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]" >
                <Cog className="w-4 h-4" />
                Logging & Monitoring
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Configure system logging and audit trail settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
                    Audit Log Retention Period (Days)
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-3 h-3 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs" style={{ fontSize: '10px' }}>
                          How long to keep audit logs before automatic deletion
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  value={settings.auditLogRetentionDays}
                  onChange={(e) => onUpdate({ auditLogRetentionDays: parseInt(e.target.value) || 365 })}
                  min={30}
                  max={2555} // 7 years
                  className="w-32"
                  style={{ fontSize: '11.2px' }}
                />
                <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                  Recommended: 365 days minimum for compliance
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      Enable Detailed Logging
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="w-3 h-3 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs" style={{ fontSize: '10px' }}>
                            Log additional system events for debugging and monitoring
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-gray-600" style={{ fontSize: '9.6px' }}>
                    Increase logging verbosity for troubleshooting
                  </p>
                </div>
                <Switch
                  checked={settings.enableDetailedLogging}
                  onCheckedChange={(checked) => onUpdate({ enableDetailedLogging: checked })}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
                    API Rate Limit (Requests per Hour)
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-3 h-3 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs" style={{ fontSize: '10px' }}>
                          Maximum API requests allowed per user per hour
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  value={settings.apiRateLimitPerHour}
                  onChange={(e) => onUpdate({ apiRateLimitPerHour: parseInt(e.target.value) || 1000 })}
                  min={100}
                  max={10000}
                  className="w-32"
                  style={{ fontSize: '11.2px' }}
                />
              </div>
            </CardContent>
          </Card> */}

          {/* Data Management */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]" >
                <Database className="w-4 h-4" />
                Data Management
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Backup, storage, and data export functionality
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      Automatic Data Backup
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="w-3 h-3 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs" style={{ fontSize: '10px' }}>
                            Enable scheduled backups of system data
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-gray-600" style={{ fontSize: '9.6px' }}>
                    Daily automated backups to secure storage
                  </p>
                </div>
                <Switch
                className=''
                  checked={settings.dataBackupEnabled}
                  onCheckedChange={(checked) => onUpdate({ dataBackupEnabled: checked })}
                />
              </div>

              <div className="space-y-3">
                <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Storage Usage</Label>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span style={{ fontSize: '11.2px' }}>{storage.used} GB used</span>
                    <span style={{ fontSize: '11.2px' }}>{storage.total} GB total</span>
                  </div>
                  <Progress value={storage.percentage} className="h-2" />
                  <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                    {storage.percentage.toFixed(1)}% of available storage used
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Data Export</Label>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDataExport}
                    disabled={isExporting}
                    variant="outline"
                    style={{ fontSize: '11.2px' }}
                  >
                    {isExporting ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export All Data
                      </>
                    )}
                  </Button>
                  <Button variant="outline" style={{ fontSize: '11.2px' }}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Audit Logs
                  </Button>
                </div>
                <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                  Export system data for backup or migration purposes
                </p>
              </div>
            </CardContent>
          </Card> */}

          {/* Save Button */}
          {/* <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              style={{ fontSize: '11.2px' }}
            >
              <Save className="w-4 h-4 mr-2" />
              Save System Settings
            </Button>
          </div> */}

          {/* Maintenance Mode Confirmation Dialog */}
          <AlertDialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Enable Maintenance Mode?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Enabling maintenance mode will restrict system access to administrators only.
                  Regular users will be unable to access the system and will see a maintenance notification.
                  <br /><br />
                  <strong>This should only be enabled during scheduled maintenance windows.</strong>
                  <br /><br />
                  Are you sure you want to enable maintenance mode?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmMaintenanceMode} className="bg-orange-600 hover:bg-orange-700">
                  Enable Maintenance Mode
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}